import { REACT_APP_BACKEND_API } from '../config';
import { getHeadersApplication } from './middleware';

const BASE_URL = REACT_APP_BACKEND_API;

/**
 * Estado de la solicitud en la máquina intake (Track PA), para las superficies
 * de cara al cliente. PRINCIPIO DE OPACIDAD (INTAKE-FLOW-STATES, 2026-09-04):
 * el cliente nunca ve estados internos crudos — el front agrupa por `category`
 * (derivada server-side) y muestra copy curado. Los campos crudos se tipan acá
 * solo para decidir el ruteo, no para mostrarse.
 */
export type IntakeApplicationCategory =
  | 'completing'
  | 'evaluating'
  | 'waiting_client'
  | 'decided';

export type IntakeApplicationStatus = {
  quoteId: string;
  /** Estado crudo de la máquina — SOLO para ruteo (pending_data_entry = seguir cargando). */
  status: string;
  displayStatus: string;
  category: IntakeApplicationCategory | string;
  hasPendingRequirements: boolean;
  /** PA-PAY (PAY-4): link de pago compuesto server-side — presente recién
   * cuando el efecto register_policy asentó la constancia policy_registered. */
  paymentUrl?: string;
};

export type IntakeApplicationRequirement = {
  id: string;
  type: string;
  status: string;
  /** Resolved client-facing title (API `client_label`; legacy `canonical_text`). */
  clientLabel: string;
  /** Optional how-to copy for the client (API `client_detail`). */
  clientDetail?: string;
};

/** GET /intake/applications/:quoteId — null si la quote no tiene fila de máquina (path legacy). */
export async function getIntakeApplicationStatus(
  quoteId: string,
): Promise<IntakeApplicationStatus | null> {
  const id = String(quoteId || '').trim();
  if (!id) return null;
  const response = await fetch(
    `${BASE_URL}intake/applications/${encodeURIComponent(id)}`,
    { method: 'GET', headers: getHeadersApplication() },
  );
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Failed to load intake application: HTTP ${response.status}`);
  }
  const payload = (await response.json()) as {
    data?: {
      quote_id?: string;
      status?: string;
      display_status?: string;
      category?: string;
      has_pending_requirements?: boolean;
      payment_url?: string;
    };
  };
  const data = payload.data ?? {};
  return {
    quoteId: data.quote_id ?? id,
    status: String(data.status ?? ''),
    displayStatus: String(data.display_status ?? data.status ?? ''),
    category: String(data.category ?? ''),
    hasPendingRequirements: Boolean(data.has_pending_requirements),
    paymentUrl: data.payment_url || undefined,
  };
}

/**
 * POST /intake/sessions/:quoteId/confirm — la conformidad del cliente (fase 3,
 * f3-7: awaiting_confirmation → confirmed). El acuse (hash de respuestas +
 * versión + texto de conformidad) lo arma el server; acá solo se dispara el
 * acto. 409 = la solicitud ya no está esperando confirmación (refrescar).
 */
export async function confirmIntakeSession(
  quoteId: string,
): Promise<{ quoteId: string; state: string }> {
  const id = String(quoteId || '').trim();
  if (!id) throw new Error('confirmIntakeSession: quoteId requerido');
  const response = await fetch(
    `${BASE_URL}intake/sessions/${encodeURIComponent(id)}/confirm`,
    { method: 'POST', headers: getHeadersApplication() },
  );
  if (!response.ok) {
    const err = new Error(`Failed to confirm intake session: HTTP ${response.status}`);
    (err as Error & { status?: number }).status = response.status;
    throw err;
  }
  const payload = (await response.json()) as {
    data?: { quoteId?: string; state?: string };
  };
  return {
    quoteId: payload.data?.quoteId ?? id,
    state: String(payload.data?.state ?? ''),
  };
}

/** GET /intake/applications/:quoteId/requirements — solo los ABIERTOS, para mostrarle al cliente qué falta. */
export async function listOpenIntakeRequirements(
  quoteId: string,
): Promise<IntakeApplicationRequirement[]> {
  const id = String(quoteId || '').trim();
  if (!id) return [];
  const response = await fetch(
    `${BASE_URL}intake/applications/${encodeURIComponent(id)}/requirements`,
    { method: 'GET', headers: getHeadersApplication() },
  );
  if (!response.ok) return [];
  const payload = (await response.json()) as {
    data?: { items?: Array<Record<string, unknown>> } | Array<Record<string, unknown>>;
  };
  const rows = Array.isArray(payload.data)
    ? payload.data
    : Array.isArray(payload.data?.items)
      ? payload.data.items
      : [];
  return rows
    .map((row) => {
      const clientDetail = String(row.clientDetail ?? row.client_detail ?? '');
      return {
        id: String(row.id ?? ''),
        type: String(row.type ?? ''),
        status: String(row.status ?? ''),
        clientLabel: String(
          row.clientLabel ??
            row.client_label ??
            row.canonicalText ??
            row.canonical_text ??
            '',
        ),
        ...(clientDetail ? { clientDetail } : {}),
      };
    })
    .filter((req) => req.status === 'requested' || req.status === 'scheduled');
}
